import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { getMe } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import {
  fetchSuperadminOrganizations,
  type SuperadminOrgOption
} from '../widget-api/superadmin-orgs';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { setEmbedSession } from '../widget-lib/embed-session-store';
import { PATH } from '../widget-lib/path-routes';
import { setSuperadminOrgId } from '../widget-lib/superadmin-org';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import { BlockLoader } from '../widget-ui/block-loader';

export default function TrainerOrgSelectPage() {
  useWidgetSeo(
    'Выбор студии | Кабинет тренера',
    'Выбор студии, в которой работает суперадмин.'
  );
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const [searchParams] = useSearchParams();
  const { accessToken, me, meLoading, orgSlug, reloadMe } = useEmbedBootstrap();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<SuperadminOrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      void fetchSuperadminOrganizations(accessToken, query)
        .then((list) => {
          if (!cancelled) {
            setItems(list);
            setError(null);
          }
        })
        .catch((e) => {
          if (cancelled) return;
          setItems([]);
          setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось загрузить студии.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [accessToken, query]);

  const choose = useCallback(
    async (org: SuperadminOrgOption) => {
      if (!accessToken) return;
      setBusyId(org.id);
      setError(null);
      try {
        setSuperadminOrgId(org.id);
        const fresh = await getMe(accessToken);
        if (fresh.organization_id == null) {
          setError('Не удалось открыть выбранную студию. Попробуйте ещё раз.');
          return;
        }
        // Сначала обновляем me в общем состоянии приложения: guard тренерских маршрутов
        // смотрит на cached me и иначе вернёт на выбор студии.
        reloadMe(fresh);
        setEmbedSession(org.slug || orgSlug || 'platform', {
          accessToken,
          organizationId: fresh.organization_id,
          embedMode: 'trainer'
        });
        const next = new URLSearchParams(searchParams);
        next.set('org_slug', org.slug || orgSlug || '');
        if (!next.get('org_slug')) next.delete('org_slug');
        const qs = next.toString();
        navigate(`${PATH.trainer}${qs ? `?${qs}` : ''}`, { replace: true });
      } catch (e) {
        setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось открыть студию.');
      } finally {
        setBusyId(null);
      }
    },
    [accessToken, navigate, orgSlug, reloadMe, searchParams]
  );

  if (!accessToken) {
    return <Navigate to={`${PATH.trainerSignIn}${suffix}`} replace />;
  }

  if (meLoading) {
    return (
      <div className="w-full min-w-0">
        <BlockLoader label="Загрузка профиля…" />
      </div>
    );
  }

  if (me && me.context && me.context.surface !== 'trainer') {
    return <Navigate to={`${PATH.home}${suffix}`} replace />;
  }

  return (
    <div className="space-y-5 w-full min-w-0">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-[var(--text)]">Выберите студию</h1>
        <p className="text-sm leading-relaxed text-[var(--text-muted)]">
          Студия, в которой вы работаете. Её можно сменить позже в профиле.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-[13px] font-medium text-[var(--text)]" htmlFor="trainer-org-search">
          Поиск по названию
        </label>
        <input
          id="trainer-org-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Название студии"
          className="min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-2 text-base text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-8 sm:py-1 sm:text-[13px]"
        />
      </div>

      {loading ? (
        <BlockLoader label="Загружаем студии…" />
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Студии не найдены.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((org) => (
            <button
              key={org.id}
              type="button"
              data-testid={`trainer-org-option-${org.id}`}
              disabled={busyId !== null}
              onClick={() => void choose(org)}
              className="group relative flex w-full items-start gap-4 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 text-left shadow-[var(--shadow-card)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--accent)] hover:shadow-md active:translate-y-0 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] disabled:pointer-events-none disabled:opacity-60"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--bg-soft)] text-[var(--accent)] transition-colors group-hover:bg-[var(--accent-bg)] group-hover:text-[var(--accent-fg)]">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-10.5h16.5M2.25 9h19.5M4.5 19.5V6.25c0-.69.56-1.25 1.25-1.25h12.5c.69 0 1.25.56 1.25 1.25v13.25" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 pr-2">
                <span className="block truncate text-base font-bold leading-snug text-[var(--text)] transition-colors group-hover:text-[var(--accent)]">
                  {org.name}
                </span>
                <span className="mt-0.5 block text-[13px] text-[var(--text-muted)]">
                  {busyId === org.id ? 'Открываем…' : 'Работать в этой студии'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {error ? (
        <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
          {error}
        </EmbedMessageBlock>
      ) : null}
    </div>
  );
}
